import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Radio,
  FormControlLabel,
  RadioGroup,
  InputAdornment,
  Divider
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import axiosClient from '../../../lib/axios';
import { useQuery } from 'react-query';
import useDebounce from '../../../lib/useDebounce';
import { CategoriaHierarquica, encontrarCaminhoNaArvore, encontrarCategoriaPorId, flattenCategories, formatarCaminhoCompleto, coletarAtributosHerdados } from './helpers';

export interface SelectCategoriaProdutoProps {
  value?: number | null;
  onChange: (value: number | null, caminhoFormatado?: string, categoria?: CategoriaHierarquica) => void;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  label?: string;
  defaultValue?: string;
}


export default function SelectCategoriaProduto({
  value,
  onChange,
  error,
  helperText,
  required,
  label = "Categoria",
  defaultValue
}: SelectCategoriaProdutoProps) {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoriaId, setSelectedCategoriaId] = useState<number | ''>(value || '');
  const [displayValue, setDisplayValue] = useState<string>(defaultValue || '');

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const fetchCategorias = async () => {
    const response = await axiosClient.get('/estoque/categoria/listar', {
      params: {
        nome: debouncedSearchTerm
      }
    });
    return response.data;
  };

  const { data } = useQuery(
    ['categorias', debouncedSearchTerm],
    () => fetchCategorias(),
    {
      keepPreviousData: true,
      staleTime: 1000 * 60 * 5,
      enabled: modalOpen
    }
  );

  const categorias = data?.data || [];
  const categoriasHierarquicas = categorias;

  useEffect(() => {
    if (value && categoriasHierarquicas.length > 0) {
      const caminho = encontrarCaminhoNaArvore(categoriasHierarquicas, value);
      if (caminho && caminho.length > 0) {
        setDisplayValue(formatarCaminhoCompleto(caminho));
        setSelectedCategoriaId(value);
      }
    } else if (!value) {
      setDisplayValue('');
      setSelectedCategoriaId('');
    }
  }, [value, categoriasHierarquicas]);

  const handleOpenModal = () => setModalOpen(true);

  const handleCloseModal = () => {
    setModalOpen(false);
    setSearchTerm('');
    setSelectedCategoriaId(value || '');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSelectedCategoriaId(value ? Number(value) : '');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSave = () => {
    if (selectedCategoriaId && typeof selectedCategoriaId === 'number') {
      const caminho = encontrarCaminhoNaArvore(categoriasHierarquicas, selectedCategoriaId);
      const categoria = encontrarCategoriaPorId(categoriasHierarquicas, selectedCategoriaId);

      if (caminho && caminho.length > 0 && categoria) {
        const atributosHerdados = coletarAtributosHerdados(caminho);
        const categoriaComAtributos: CategoriaHierarquica = {
          ...categoria,
          atributos: atributosHerdados
        };

        const caminhoFormatado = formatarCaminhoCompleto(caminho);
        setDisplayValue(caminhoFormatado);
        onChange(selectedCategoriaId, caminhoFormatado, categoriaComAtributos);
      }
    }
    handleCloseModal();
  };

  const categoriasAchatadas = useMemo(
    () => flattenCategories(categoriasHierarquicas),
    [categoriasHierarquicas]
  );

  return (
    <>
      <TextField
        label={label}
        fullWidth
        required={required}
        value={displayValue}
        defaultValue={defaultValue}
        onClick={handleOpenModal}
        slotProps={{ input: { readOnly: true } }}
        error={error}
        helperText={helperText}
        placeholder="Clique para selecionar"
      />

      <Dialog open={modalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle>Selecionar Categoria</DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar categoria..."
              value={searchTerm}
              onChange={handleSearchChange}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }
              }}
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ maxHeight: '400px', overflowY: 'auto', p: 1 }}>
            <RadioGroup
              name="categoria_id"
              value={selectedCategoriaId}
              onChange={handleChange}
            >
              {categoriasAchatadas.map((categoria) => (
                <Box key={categoria.id}>
                  <FormControlLabel
                    value={categoria.id}
                    control={<Radio />}
                    label={
                      <Typography variant="body1">
                        {categoria.pathLabel}
                      </Typography>
                    }
                  />
                </Box>
              ))}

              {categoriasAchatadas.length === 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
                  Nenhuma categoria encontrada
                </Typography>
              )}
            </RadioGroup>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleCloseModal} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!selectedCategoriaId}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}